import React, {
  Suspense,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { WhatsAppIcon, PhoneCallIcon } from './icons';
import { PHONE, waUrl, waMessages, reportConversion } from '../utils/leads';

// three.js is ~150KB gzip — load it after first paint so mobile stays fast
const HeroScene = React.lazy(() => import('./HeroScene'));

gsap.registerPlugin(ScrollTrigger);

/* ---------------------------------- data --------------------------------- */

const services = [
  {
    name: 'Business Websites',
    price: '₹12,999',
    tagline: 'Look established. Get found. Get enquiries.',
    features: [
      'Designed to look premium on every phone',
      'Google-ready — SEO, speed & Maps setup',
      'WhatsApp & call buttons that bring leads',
      'First version live in about 7 days',
    ],
    message: waMessages.website,
    accent: 'from-orange-500/20 to-transparent',
    badge: null,
  },
  {
    name: 'Mobile Apps',
    price: '₹19,999',
    tagline: 'Your business in your customer’s pocket.',
    features: [
      'Android & iOS from a single budget',
      'Payments, push notifications & logins',
      'Play Store / App Store publishing included',
      'Admin dashboard to run it yourself',
    ],
    message: waMessages.app,
    accent: 'from-emerald-500/20 to-transparent',
    badge: 'Most popular',
  },
  {
    name: 'AI-Powered Apps',
    price: '₹24,999',
    tagline: 'Put AI to work inside your business.',
    features: [
      'WhatsApp & chat automation for support',
      'AI content, image & document generation',
      'Custom AI features built into your app',
      'Powered by the latest AI models',
    ],
    message: waMessages.ai,
    accent: 'from-violet-500/20 to-transparent',
    badge: null,
  },
];

const projects = [
  {
    name: 'KiranaGo',
    kind: 'Quick-commerce delivery app',
    blurb:
      'Blinkit-style grocery ordering with live order tracking and a store-owner dashboard.',
    tags: ['React Native', 'Node.js', 'Maps & Tracking'],
    color: '#22c55e',
    mock: 'commerce' as const,
  },
  {
    name: 'Beacon',
    kind: 'Social media + ads manager',
    blurb:
      'One dashboard to plan posts and run Meta ad campaigns — built for small-business founders.',
    tags: ['Expo', 'Meta APIs', 'Analytics'],
    color: '#8b5cf6',
    mock: 'analytics' as const,
  },
  {
    name: 'AllSecure VPN',
    kind: 'Privacy app for Android',
    blurb:
      'One-tap WireGuard VPN with a native Kotlin engine for fast, secure connections.',
    tags: ['Android', 'Kotlin', 'WireGuard'],
    color: '#22d3ee',
    mock: 'vpn' as const,
  },
  {
    name: 'OneMarket',
    kind: 'Marketplace platform — client work',
    blurb:
      'A three-app marketplace (buyers, sellers, admin) built and shipped to the App Store for an international client.',
    tags: ['iOS & Android', 'Payments', '3 apps'],
    color: '#f59e0b',
    mock: 'market' as const,
  },
];

const steps = [
  {
    title: 'Say hi on WhatsApp',
    text: 'Tell us your idea in plain words — Hindi or English, no tech talk needed.',
  },
  {
    title: 'Free plan & fixed quote in 24h',
    text: 'You get a clear scope, a fixed price and a timeline. No surprises later.',
  },
  {
    title: 'We build, you watch',
    text: 'Demo links and regular updates while your product takes shape.',
  },
  {
    title: 'Launch & grow',
    text: 'We put it live, hand over everything, and stay around for support.',
  },
];

const faqs = [
  {
    q: 'How much will my website or app cost?',
    a: 'Websites start at ₹12,999, mobile apps at ₹19,999 and AI apps at ₹24,999. After one WhatsApp chat about what you need, you get a fixed quote — the price we agree is the price you pay.',
  },
  {
    q: 'How fast will it be ready?',
    a: 'Most websites have a first working version live in about 7 days. Apps typically take 2–4 weeks depending on features. You see progress through demo links while we build.',
  },
  {
    q: 'I don’t know anything about tech. Is that a problem?',
    a: 'Not at all — most of our clients are business owners, not engineers. You explain your business on WhatsApp, we handle everything technical: design, build, domain, hosting and publishing.',
  },
  {
    q: 'Will you publish my app on the Play Store and App Store?',
    a: 'Yes. Publishing is part of the job — store accounts, screenshots, descriptions and review process. We have shipped apps to both stores, including for international clients.',
  },
  {
    q: 'What happens after launch?',
    a: 'You own the code and all accounts — no lock-in. We stay available on WhatsApp for fixes and improvements, and you can extend with new features anytime.',
  },
];

const techStack = [
  'React',
  'React Native',
  'Next.js',
  'Node.js',
  'Kotlin',
  'Swift',
  'Three.js',
  'PostgreSQL',
  'AI / LLMs',
  'AWS & Cloud',
  'Supabase',
  'Express',
];

/* ------------------------------- sub-pieces ------------------------------- */

const WaButton: React.FC<{
  message: string;
  className?: string;
  children: React.ReactNode;
}> = ({ message, className = 'btn-whatsapp', children }) => {
  const url = waUrl(message);
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className={className}
      onClick={() => reportConversion(url)}
    >
      <WhatsAppIcon className="w-5 h-5 flex-shrink-0" />
      {children}
    </a>
  );
};

/** Pure-CSS phone mockup with a per-project skeleton UI. */
const PhoneMock: React.FC<{ variant: string; color: string }> = ({
  variant,
  color,
}) => (
  <div className="relative w-[150px] h-[300px] xs:w-[170px] xs:h-[340px] rounded-[2rem] border-[6px] border-ink-600 bg-ink-900 shadow-2xl overflow-hidden flex-shrink-0">
    {/* notch */}
    <div className="absolute top-1.5 left-1/2 -translate-x-1/2 w-16 h-3.5 bg-ink-600 rounded-full z-10" />
    <div className="absolute inset-0 pt-8 px-3 pb-3 flex flex-col gap-2">
      {variant === 'commerce' && (
        <>
          <div className="h-7 rounded-lg bg-white/10" />
          <div className="grid grid-cols-2 gap-2 flex-1">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="rounded-lg bg-white/[0.06] p-1.5 flex flex-col gap-1">
                <div className="flex-1 rounded-md" style={{ background: `${color}26` }} />
                <div className="h-1.5 w-3/4 rounded bg-white/20" />
                <div className="h-1.5 w-1/2 rounded" style={{ background: `${color}99` }} />
              </div>
            ))}
          </div>
          <div
            className="h-9 rounded-lg flex items-center justify-center text-[9px] font-bold text-ink-950"
            style={{ background: color }}
          >
            Delivery in 15 min
          </div>
        </>
      )}
      {variant === 'analytics' && (
        <>
          <div className="h-7 rounded-lg bg-white/10" />
          <div className="rounded-lg bg-white/[0.06] p-2 flex items-end gap-1.5 h-24">
            {[40, 65, 50, 85, 70, 95].map((h, i) => (
              <div
                key={i}
                className="flex-1 rounded-t"
                style={{ height: `${h}%`, background: i === 5 ? color : `${color}55` }}
              />
            ))}
          </div>
          {[...Array(3)].map((_, i) => (
            <div key={i} className="rounded-lg bg-white/[0.06] p-2 flex gap-2 items-center">
              <div className="w-7 h-7 rounded-md" style={{ background: `${color}33` }} />
              <div className="flex-1 space-y-1">
                <div className="h-1.5 w-full rounded bg-white/20" />
                <div className="h-1.5 w-1/2 rounded bg-white/10" />
              </div>
            </div>
          ))}
          <div
            className="h-9 rounded-lg flex items-center justify-center text-[9px] font-bold text-white"
            style={{ background: color }}
          >
            Boost post
          </div>
        </>
      )}
      {variant === 'vpn' && (
        <>
          <div className="h-7 rounded-lg bg-white/10" />
          <div className="flex-1 flex flex-col items-center justify-center gap-3">
            <div
              className="w-24 h-24 rounded-full flex items-center justify-center"
              style={{ background: `${color}1a`, boxShadow: `0 0 40px ${color}66` }}
            >
              <div
                className="w-16 h-16 rounded-full flex items-center justify-center text-[9px] font-bold text-ink-950"
                style={{ background: color }}
              >
                ON
              </div>
            </div>
            <div className="px-3 py-1 rounded-full text-[9px] font-semibold" style={{ background: `${color}26`, color }}>
              Connected · Mumbai
            </div>
          </div>
          <div className="h-9 rounded-lg bg-white/[0.06]" />
        </>
      )}
      {variant === 'market' && (
        <>
          <div className="h-7 rounded-lg bg-white/10" />
          {[...Array(3)].map((_, i) => (
            <div key={i} className="rounded-lg bg-white/[0.06] p-2 flex gap-2">
              <div className="w-12 h-12 rounded-md" style={{ background: `${color}26` }} />
              <div className="flex-1 space-y-1.5 py-1">
                <div className="h-1.5 w-full rounded bg-white/20" />
                <div className="h-1.5 w-2/3 rounded bg-white/10" />
                <div className="h-2 w-1/3 rounded font-bold" style={{ background: `${color}99` }} />
              </div>
            </div>
          ))}
          <div
            className="h-9 rounded-lg flex items-center justify-center text-[9px] font-bold text-ink-950 mt-auto"
            style={{ background: color }}
          >
            Buy now
          </div>
        </>
      )}
    </div>
  </div>
);

/* --------------------------------- page ---------------------------------- */

const Home: React.FC = () => {
  const rootRef = useRef<HTMLDivElement>(null);
  const [openFaq, setOpenFaq] = useState<number | null>(0);
  const [showStickyBar, setShowStickyBar] = useState(false);

  // sticky mobile CTA appears once the hero CTAs scroll away
  useEffect(() => {
    const onScroll = () => setShowStickyBar(window.scrollY > 560);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useLayoutEffect(() => {
    const root = rootRef.current;
    if (!root) return;

    // make the timeline rails span from the first circle's centre to the
    // last circle's centre instead of the full track height
    const sizeProcessRails = () => {
      const track = root.querySelector<HTMLElement>('.process-track');
      const items = track?.querySelectorAll<HTMLElement>('.process-step');
      if (!track || !items || items.length === 0) return;
      const first = items[0];
      const last = items[items.length - 1];
      const top = first.offsetTop + 22; // circle is h-11 → centre at 22px
      const bottom = track.offsetHeight - last.offsetTop - 22;
      track.querySelectorAll<HTMLElement>('.process-rail').forEach((el) => {
        el.style.top = `${top}px`;
        el.style.bottom = `${bottom}px`;
      });
    };
    sizeProcessRails();
    window.addEventListener('resize', sizeProcessRails);
    window.addEventListener('load', sizeProcessRails);
    const removeRailListeners = () => {
      window.removeEventListener('resize', sizeProcessRails);
      window.removeEventListener('load', sizeProcessRails);
    };

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      return removeRailListeners;
    }

    root.classList.add('gsap-ready');

    const ctx = gsap.context(() => {
      // hero entrance
      gsap
        .timeline({ defaults: { ease: 'power3.out' } })
        .fromTo(
          '.hero-el',
          { y: 36, opacity: 0 },
          { y: 0, opacity: 1, duration: 0.9, stagger: 0.12 }
        );

      // generic scroll reveals
      gsap.utils.toArray<HTMLElement>('[data-reveal]').forEach((el) => {
        gsap.fromTo(
          el,
          { y: 32, opacity: 0 },
          {
            y: 0,
            opacity: 1,
            duration: 0.8,
            ease: 'power3.out',
            scrollTrigger: { trigger: el, start: 'top 88%' },
          }
        );
      });

      // process timeline line draws as you scroll
      gsap.fromTo(
        '.process-line',
        { scaleY: 0 },
        {
          scaleY: 1,
          transformOrigin: 'top',
          ease: 'none',
          scrollTrigger: {
            trigger: '.process-track',
            start: 'top 75%',
            end: 'bottom 60%',
            scrub: 0.6,
          },
        }
      );
    }, root);

    return () => {
      removeRailListeners();
      root.classList.remove('gsap-ready');
      ctx.revert();
    };
  }, []);

  return (
    <div ref={rootRef} className="bg-ink-950 text-white overflow-hidden">
      {/* ================================ HERO ================================ */}
      <section className="relative min-h-[100svh] flex items-center pt-24 pb-28 md:pb-20">
        {/* ambient glows (also the no-WebGL fallback) */}
        <div className="absolute -top-32 -right-32 w-[34rem] h-[34rem] glow-ember rounded-full" />
        <div className="absolute bottom-0 -left-40 w-[28rem] h-[28rem] glow-ember rounded-full opacity-60" />
        <Suspense fallback={null}>
          <HeroScene />
        </Suspense>

        <div className="relative max-w-7xl mx-auto px-5 sm:px-6 lg:px-8 w-full">
          <div className="max-w-3xl">
            <div className="hero-el inline-flex items-center gap-2 glass px-4 py-2 text-sm text-white/80 mb-7">
              <span className="w-2 h-2 rounded-full bg-whatsapp animate-pulse-slow" />
              Software studio · Replies on WhatsApp in minutes
            </div>

            <h1 className="hero-el font-display font-bold leading-[1.04] text-[2.6rem] xs:text-5xl md:text-6xl lg:text-7xl tracking-tight mb-6">
              Big-brand tech.
              <span className="block text-gradient">Small-business price.</span>
            </h1>

            <p className="hero-el text-lg md:text-xl text-white/65 leading-relaxed max-w-xl mb-9">
              JSONPLAY designs and ships fast, beautiful websites, mobile apps
              and AI products for ambitious Indian businesses —{' '}
              <span className="text-white font-semibold">
                live in days, from ₹12,999.
              </span>
            </p>

            <div className="hero-el flex flex-col sm:flex-row gap-3.5 mb-12">
              <WaButton message={waMessages.general}>
                Chat on WhatsApp — it’s free
              </WaButton>
              <a href="#work" className="btn-ghost">
                See what we’ve built ↓
              </a>
            </div>

            <div className="hero-el grid grid-cols-3 gap-3 max-w-lg">
              {[
                ['₹12,999', 'projects start at'],
                ['7 days', 'to first launch'],
                ['100%', 'code ownership'],
              ].map(([big, small]) => (
                <div key={small} className="glass px-3 py-4 text-center">
                  <div className="font-display font-bold text-lg xs:text-xl text-ember-glow">
                    {big}
                  </div>
                  <div className="text-[11px] xs:text-xs text-white/50 mt-1">
                    {small}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ============================ TECH MARQUEE ============================ */}
      <section className="border-y border-white/[0.06] bg-ink-900/60 py-5 overflow-hidden">
        <div className="flex w-max animate-marquee gap-12 pr-12">
          {[...techStack, ...techStack].map((t, i) => (
            <span
              key={i}
              className="font-display text-sm tracking-widest uppercase text-white/30 whitespace-nowrap"
            >
              {t}
            </span>
          ))}
        </div>
      </section>

      {/* ============================== SERVICES ============================== */}
      <section id="services" className="relative py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8">
          <div data-reveal className="max-w-2xl mb-14">
            <span className="eyebrow">What we build</span>
            <h2 className="font-display font-bold text-3xl md:text-5xl tracking-tight mt-4 mb-4">
              Pick what your business needs.
              <span className="text-white/40"> We handle the rest.</span>
            </h2>
            <p className="text-white/60 text-lg">
              Fixed prices, clear timelines, and everything included — design,
              build, hosting and launch.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-5">
            {services.map((s) => (
              <div
                key={s.name}
                data-reveal
                className="relative glass p-7 flex flex-col hover:border-ember/50 transition-colors duration-300"
              >
                <div
                  className={`absolute inset-x-0 top-0 h-32 rounded-t-2xl bg-gradient-to-b ${s.accent} pointer-events-none`}
                />
                {s.badge && (
                  <span className="absolute -top-3 right-5 bg-ember text-ink-950 text-xs font-bold px-3 py-1 rounded-full">
                    {s.badge}
                  </span>
                )}
                <h3 className="relative font-display font-bold text-2xl mb-1">
                  {s.name}
                </h3>
                <p className="relative text-white/55 text-sm mb-5">{s.tagline}</p>
                <div className="relative mb-6">
                  <span className="text-white/40 text-sm">from </span>
                  <span className="font-display font-bold text-4xl text-ember-glow">
                    {s.price}
                  </span>
                </div>
                <ul className="relative space-y-3 mb-8 flex-1">
                  {s.features.map((f) => (
                    <li key={f} className="flex gap-3 text-sm text-white/75">
                      <svg
                        className="w-5 h-5 text-ember flex-shrink-0"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                          clipRule="evenodd"
                        />
                      </svg>
                      {f}
                    </li>
                  ))}
                </ul>
                <WaButton
                  message={s.message}
                  className="btn-whatsapp w-full !py-3.5 text-sm"
                >
                  Get a free quote
                </WaButton>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============================== PORTFOLIO ============================= */}
      <section id="work" className="relative py-24 md:py-32 bg-ink-900/40">
        <div className="absolute top-1/3 left-1/2 -translate-x-1/2 w-[40rem] h-[40rem] glow-ember opacity-50 pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-5 sm:px-6 lg:px-8">
          <div data-reveal className="max-w-2xl mb-14">
            <span className="eyebrow">Our work</span>
            <h2 className="font-display font-bold text-3xl md:text-5xl tracking-tight mt-4 mb-4">
              Real products. <span className="text-white/40">Shipped.</span>
            </h2>
            <p className="text-white/60 text-lg">
              Not stock templates — products we designed, engineered and put in
              users’ hands.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 gap-5">
            {projects.map((p) => (
              <div
                key={p.name}
                data-reveal
                className="glass p-6 sm:p-7 flex gap-5 items-center overflow-hidden group"
              >
                <div className="transition-transform duration-500 group-hover:-translate-y-2 group-hover:rotate-1">
                  <PhoneMock variant={p.mock} color={p.color} />
                </div>
                <div className="min-w-0">
                  <div
                    className="text-xs font-semibold tracking-wide uppercase mb-1.5"
                    style={{ color: p.color }}
                  >
                    {p.kind}
                  </div>
                  <h3 className="font-display font-bold text-xl xs:text-2xl mb-2">
                    {p.name}
                  </h3>
                  <p className="text-white/60 text-sm leading-relaxed mb-4">
                    {p.blurb}
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    {p.tags.map((t) => (
                      <span
                        key={t}
                        className="text-[11px] px-2.5 py-1 rounded-full bg-white/[0.06] border border-white/10 text-white/60"
                      >
                        {t}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div data-reveal className="text-center mt-12">
            <WaButton message={waMessages.quote} className="btn-ghost">
              Want something like this? Let’s talk
            </WaButton>
          </div>
        </div>
      </section>

      {/* =============================== PROCESS ============================== */}
      <section className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8">
          <div data-reveal className="max-w-2xl mb-14">
            <span className="eyebrow">How it works</span>
            <h2 className="font-display font-bold text-3xl md:text-5xl tracking-tight mt-4 mb-4">
              From idea to live —{' '}
              <span className="text-white/40">in 4 steps.</span>
            </h2>
          </div>

          <div className="process-track relative max-w-2xl mx-auto">
            {/* track + animated fill — JS sizes both to end at the last circle */}
            <div className="process-rail absolute left-[22px] top-2 bottom-2 w-px bg-white/10" />
            <div className="process-rail process-line absolute left-[22px] top-2 bottom-2 w-px bg-gradient-to-b from-ember to-ember-glow" />

            <div className="space-y-10">
              {steps.map((step, i) => (
                <div
                  key={step.title}
                  data-reveal
                  className="process-step relative pl-16"
                >
                  <div className="absolute left-0 top-0 w-11 h-11 rounded-full glass-strong flex items-center justify-center font-display font-bold text-ember">
                    {i + 1}
                  </div>
                  <h3 className="font-display font-bold text-xl mb-1.5">
                    {step.title}
                  </h3>
                  <p className="text-white/60 leading-relaxed">{step.text}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* =============================== WHY US =============================== */}
      <section className="py-20 bg-ink-900/40 border-y border-white/[0.06]">
        <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              ['Fixed price', 'The quote we agree on is what you pay. No hourly meters.'],
              ['Built in-house', 'Engineers who ship real products — nothing outsourced.'],
              ['You own everything', 'Code, accounts, domain — handed over fully. Zero lock-in.'],
              ['Support that stays', 'We’re one WhatsApp message away after launch.'],
            ].map(([title, text]) => (
              <div key={title} data-reveal className="glass p-6">
                <h3 className="font-display font-bold text-lg mb-2 text-ember-glow">
                  {title}
                </h3>
                <p className="text-sm text-white/60 leading-relaxed">{text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ================================= FAQ ================================ */}
      <section className="py-24 md:py-32">
        <div className="max-w-3xl mx-auto px-5 sm:px-6 lg:px-8">
          <div data-reveal className="mb-12">
            <span className="eyebrow">Questions</span>
            <h2 className="font-display font-bold text-3xl md:text-5xl tracking-tight mt-4">
              Before you ask.
            </h2>
          </div>

          <div className="space-y-3">
            {faqs.map((f, i) => {
              const open = openFaq === i;
              return (
                <div key={f.q} data-reveal className="glass overflow-hidden">
                  <button
                    onClick={() => setOpenFaq(open ? null : i)}
                    className="w-full flex items-center justify-between gap-4 px-6 py-5 text-left"
                    aria-expanded={open}
                  >
                    <span className="font-display font-semibold text-base md:text-lg">
                      {f.q}
                    </span>
                    <span
                      className={`text-ember text-2xl leading-none transition-transform duration-300 flex-shrink-0 ${
                        open ? 'rotate-45' : ''
                      }`}
                    >
                      +
                    </span>
                  </button>
                  <div
                    className={`grid transition-all duration-300 ease-out ${
                      open ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'
                    }`}
                  >
                    <div className="overflow-hidden">
                      <p className="px-6 pb-6 text-white/65 leading-relaxed">
                        {f.a}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ============================== FINAL CTA ============================= */}
      <section className="relative py-24 md:py-32">
        <div className="max-w-5xl mx-auto px-5 sm:px-6 lg:px-8">
          <div
            data-reveal
            className="relative rounded-3xl overflow-hidden bg-gradient-to-br from-ember-deep via-ember to-ember-soft p-10 md:p-16 text-center"
          >
            <div className="absolute -top-24 -right-24 w-72 h-72 bg-white/15 rounded-full blur-3xl" />
            <div className="absolute -bottom-24 -left-24 w-72 h-72 bg-ink-950/30 rounded-full blur-3xl" />
            <h2 className="relative font-display font-bold text-3xl md:text-5xl tracking-tight text-ink-950 mb-4">
              Tell us your idea today.
              <span className="block">See it taking shape this week.</span>
            </h2>
            <p className="relative text-ink-950/75 text-lg max-w-xl mx-auto mb-9 font-medium">
              One WhatsApp message gets you a free plan and a fixed quote
              within 24 hours. No calls you didn’t ask for, no pressure.
            </p>
            <div className="relative flex flex-col sm:flex-row gap-3.5 justify-center">
              <WaButton
                message={waMessages.quote}
                className="inline-flex items-center justify-center gap-2.5 bg-ink-950 text-white font-semibold rounded-full px-8 py-4 hover:bg-ink-900 transition-colors active:scale-[0.97]"
              >
                Get my free quote
              </WaButton>
              <a
                href={`tel:${PHONE}`}
                className="inline-flex items-center justify-center gap-2.5 border-2 border-ink-950/30 text-ink-950 font-semibold rounded-full px-8 py-4 hover:bg-ink-950/10 transition-colors active:scale-[0.97]"
              >
                <PhoneCallIcon className="w-5 h-5" />
                Call us instead
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* ===================== STICKY MOBILE LEAD BAR ===================== */}
      <div
        className={`md:hidden fixed bottom-0 inset-x-0 z-40 transition-transform duration-300 ${
          showStickyBar ? 'translate-y-0' : 'translate-y-full'
        }`}
      >
        <div className="bg-ink-900/95 backdrop-blur-lg border-t border-white/10 px-4 pt-3 pb-3 pb-safe flex gap-3">
          <WaButton
            message={waMessages.general}
            className="btn-whatsapp flex-1 !py-3.5 text-sm"
          >
            WhatsApp us
          </WaButton>
          <a
            href={`tel:${PHONE}`}
            className="btn-ghost !py-3.5 !px-5 text-sm"
            aria-label="Call JSONPLAY"
          >
            <PhoneCallIcon className="w-5 h-5" />
            Call
          </a>
        </div>
      </div>
    </div>
  );
};

export default Home;
